import { Link } from "react-router-dom";
import { displayTz } from "@/lib/prefs";
import { tipoff } from "@/lib/format";

export const gameDate = (iso, opts = { weekday: "short", month: "short", day: "numeric" }) =>
  new Date(iso).toLocaleDateString("en-US", { timeZone: displayTz(), ...opts });

export function Card({ title, action, onAction, children, className = "", id }) {
  return (
    <section className={`tm-card ${className}`} data-testid={`team-card-${id}`}>
      <div className="tm-card-head"><h3>{title}</h3>{action && <button type="button" onClick={onAction} data-testid={`team-card-${id}-more`}>{action}</button>}</div>
      {children}
    </section>
  );
}

export function Result({ g }) {
  if (g.state !== "post") return <span className="tm-time">{tipoff(g.date)}</span>;
  return <span className={`tm-res ${g.win ? "is-win" : "is-loss"}`}><b>{g.win ? "W" : "L"}</b> {g.pts}-{g.opp_pts}</span>;
}

export function Opp({ g }) {
  return (
    <span className="tm-opp"><em>{g.home ? "vs" : "@"}</em><img src={g.opp_logo} alt="" loading="lazy" />{g.opp_name || g.opp_code}</span>
  );
}

export function StandingsTable({ rows, highlight, id }) {
  return (
    <table className="tm-table" data-testid={`team-standings-${id}`}>
      <thead><tr><th>#</th><th>Team</th><th>W</th><th>L</th><th>PCT</th><th>GB</th><th>STRK</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.team_id} className={r.team_id === highlight ? "is-me" : ""}>
            <td>{id === "conference" ? r.seed : i + 1}</td>
            <td><Link to={`/teams/${r.team_id}`} className="tm-opp"><img src={r.logo} alt="" loading="lazy" />{r.name}</Link></td>
            <td>{r.wins}</td><td>{r.losses}</td><td>{r.pct}</td><td>{r.gb}</td><td>{r.streak}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NextGame({ page }) {
  const g = page.schedule.find((x) => x.state !== "post");
  if (!g) return <p className="tm-empty">No upcoming games.</p>;
  const me = page.info;
  const sides = [{ code: me.code, logo: me.logo, rec: page.standing && `${page.standing.wins}-${page.standing.losses}` }, { code: g.opp_code, logo: g.opp_logo }];
  const [away, home] = g.home ? [sides[1], sides[0]] : sides;
  return (
    <div className="tm-next" data-testid="team-next-game">
      {[away, home].map((s, i) => (
        <div key={i} className="tm-next-team"><img src={s.logo} alt="" /><b>{s.code}</b>{s.rec && <small>{s.rec}</small>}</div>
      )).reduce((acc, el, i) => (i ? [...acc, (
        <div key="mid" className="tm-next-mid">
          <span>{g.season_type === 3 ? "Playoffs" : "Regular season"}</span>
          <b>{gameDate(g.date)}</b><strong>{tipoff(g.date)}</strong><small>{g.venue}</small>
        </div>), el] : [el]), [])}
    </div>
  );
}

export function SeasonStats({ rows, limit }) {
  if (!rows.length) return <p className="tm-empty">No box scores yet this season.</p>;
  return (
    <div className="tm-stats" data-testid="team-season-stats">
      {rows.slice(0, limit).map((s) => (
        <div key={s.key} className="tm-stat">
          <span>{s.label}</span><b>{s.value}{s.pct ? "%" : ""}</b>
          <i><em style={{ width: `${Math.max(6, 100 - ((s.rank - 1) / Math.max(1, s.of - 1)) * 94)}%` }} /></i>
          <small>{s.rank}{["th", "st", "nd", "rd"][(s.rank % 100 > 10 && s.rank % 100 < 14) || s.rank % 10 > 3 ? 0 : s.rank % 10]}</small>
        </div>
      ))}
    </div>
  );
}

export function OverviewTab({ page, onTab }) {
  const info = page.info;
  const recent = page.schedule.filter((g) => g.state === "post").slice(-5).reverse();
  const facts = [["City", info.city], ["Arena", info.arena], ["Head coach", info.coach], ["Conference", page.standing?.conference],
    ["Division", page.standing?.division], ["Home", page.standing?.home], ["Road", page.standing?.road], ["Last 10", page.standing?.last10]];
  return (
    <div className="tm-grid">
      <Card title="Next game" id="next" className="tm-span-5"><NextGame page={page} /></Card>
      <Card title="Season stats" id="season" className="tm-span-4" action="All stats" onAction={() => onTab("stats")}>
        <SeasonStats rows={page.season_stats} limit={5} />
      </Card>
      <Card title="Team info" id="info" className="tm-span-3">
        <dl className="tm-facts">{facts.filter(([, v]) => v).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      </Card>
      <Card title="Recent games" id="recent" className="tm-span-5" action="Schedule" onAction={() => onTab("schedule")}>
        {recent.length ? (
          <table className="tm-table"><tbody>
            {recent.map((g) => <tr key={g.id}><td>{gameDate(g.date, { month: "short", day: "numeric" })}</td><td><Opp g={g} /></td><td className="tm-right"><Result g={g} /></td></tr>)}
          </tbody></table>
        ) : <p className="tm-empty">No games played yet.</p>}
      </Card>
      <Card title={`${page.standing?.division || ""} standings`} id="division" className="tm-span-4">
        <StandingsTable rows={page.division} highlight={page.team_id} id="division" />
      </Card>
      <Card title="Latest news" id="news" className="tm-span-3" action="All news" onAction={() => onTab("news")}>
        {page.news.slice(0, 3).map((n, i) => (
          <a key={i} className="tm-news-mini" href={n.link || undefined} target="_blank" rel="noreferrer">
            {n.image && <img src={n.image} alt="" loading="lazy" />}<span>{n.headline}<small>{n.published ? gameDate(n.published, { month: "short", day: "numeric" }) : ""}</small></span>
          </a>
        ))}
        {!page.news.length && <p className="tm-empty">No news right now.</p>}
      </Card>
    </div>
  );
}
